import React from "react"
import { FileText } from "lucide-react"
import TopicPageLayout from "../topic-page-layout"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../../../../ui/accordion"

const HelpDashboard = () => {
  const faqItems = [
    {
      question: "How do I interpret my analytics data?",
      answer:
        "Your analytics dashboard shows key metrics like views, engagement rate, and revenue. The graphs show trends over time, helping you identify what content performs best with your audience. Hover over data points for more details."
    },
    {
      question: "Can I export my analytics data?",
      answer:
        "Yes, you can export your data as CSV or Excel files. Simply click the 'Export' button in the top right corner of your analytics dashboard and select your preferred format."
    },
    {
      question: "How often is my dashboard updated?",
      answer:
        "Most metrics are updated in real-time or with a slight delay of 5-10 minutes. Financial data may take up to 24 hours to fully update, especially for transactions that are still processing."
    },
    {
      question: "How can I customize my dashboard view?",
      answer:
        "You can customize your dashboard by clicking the 'Customize' button in the top right corner. From there, you can drag and drop widgets, hide or show specific metrics, and save different layouts for different purposes."
    }
  ]

  const dashboardFeatures = [
    {
      title: "Analytics Overview",
      description:
        "Track your performance metrics, audience growth, and engagement rates."
    },
    {
      title: "Content Management",
      description:
        "Upload, organize, and schedule your content from a central hub."
    },
    {
      title: "Audience Insights",
      description:
        "Understand your audience demographics, preferences, and behavior."
    },
    {
      title: "Financial Reports",
      description:
        "Monitor your earnings, transaction history, and payout schedules."
    }
  ]

  return (
    <TopicPageLayout
      title="Your Dashboard"
      description="Navigate and make the most of your creator dashboard with these tips and guides."
      icon={<FileText className="h-8 w-8 text-[#3D2A60]" />}
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Dashboard Overview
          </h2>
          <p className="text-gray-600 mb-6">
            Your dashboard is your command center for managing all aspects of
            your creator business. It's designed to give you quick access to
            important information and tools to help you grow.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dashboardFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white shadow-sm border border-gray-100 rounded-lg p-5"
              >
                <h3 className="text-lg font-medium text-[#3D2A60] mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Getting the Most Out of Your Analytics
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-[#3D2A60] mb-2">
                Understanding Key Metrics
              </h3>
              <p className="text-gray-600 mb-3">
                Focus on these core metrics to gauge your performance:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-gray-600">
                <li>
                  <span className="font-medium">Engagement Rate:</span> How
                  much your audience interacts with your content
                </li>
                <li>
                  <span className="font-medium">Conversion Rate:</span> The
                  percentage of viewers who become paying supporters
                </li>
                <li>
                  <span className="font-medium">Retention Rate:</span> How
                  many supporters continue their subscriptions month-to-month
                </li>
                <li>
                  <span className="font-medium">
                    Average Revenue Per User (ARPU):
                  </span>{" "}
                  Your average earnings per supporter
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-[#3D2A60] mb-2">
                Setting Goals
              </h3>
              <p className="text-gray-600">
                Use the Goals feature in your dashboard to set targets for
                growth. The system will track your progress and provide
                suggestions to help you reach your objectives. Goals can be
                set for audience size, engagement rates, or revenue targets.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Dashboard Tips & Shortcuts
          </h2>
          <div className="bg-[#F4F0FA]/50 p-6 rounded-lg">
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="w-5 h-5 bg-[#3D2A60] text-white rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">
                  1
                </span>
                <div>
                  <p className="text-gray-800">
                    <span className="font-medium">Quick Navigation:</span> Use
                    the sidebar shortcuts to jump between sections quickly
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="w-5 h-5 bg-[#3D2A60] text-white rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">
                  2
                </span>
                <div>
                  <p className="text-gray-800">
                    <span className="font-medium">Date Ranges:</span> Adjust
                    time periods using the calendar selector at the top of
                    analytics pages
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="w-5 h-5 bg-[#3D2A60] text-white rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">
                  3
                </span>
                <div>
                  <p className="text-gray-800">
                    <span className="font-medium">Notifications:</span> Click
                    the bell icon to see important alerts and updates
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="w-5 h-5 bg-[#3D2A60] text-white rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">
                  4
                </span>
                <div>
                  <p className="text-gray-800">
                    <span className="font-medium">Search:</span> Use the
                    global search function to quickly find content,
                    supporters, or settings
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-[#3D2A60]">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-gray-600">{item.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>
    </TopicPageLayout>
  )
}

export default HelpDashboard
